import React, { createContext, useContext, useRef } from 'react';
import useFilterLogic from './FilterLogic';

/**
 * Context for filter logic - ensures hooks run only ONCE per app
 * This prevents useFilterLogic() from being called in 10+ components
 * which was causing 10+ simultaneous API requests
 */
const FilterLogicContext = createContext(null);

/**
 * Provider component - wrap your app with this at the top level
 * This should wrap the entire app, typically in App.tsx or main layout
 *
 * Example:
 * <FilterLogicProvider>
 *   <YourApp />
 * </FilterLogicProvider>
 */
export const FilterLogicProvider = ({ children }) => {
	// Initialize all filter hooks ONCE per app mount
	// All components will share this same instance
	const filterLogic = useFilterLogic();

	return (
		<FilterLogicContext.Provider value={filterLogic}>
			{children}
		</FilterLogicContext.Provider>
	);
};

/**
 * Hook to access filter logic from anywhere in the app
 *
 * Usage in components:
 * const FILTER = useFilterLogicContext();
 *
 * This replaces the old pattern:
 * const FILTER = useFilterLogic();
 *
 * Benefits:
 * - useFilterLogic() runs once (at provider mount)
 * - All 10+ components share the same instance
 * - No duplicate API calls
 * - No redundant hook initializations
 */
export const useFilterLogicContext = () => {
	const context = useContext(FilterLogicContext);

	if (!context) {
		throw new Error(
			'useFilterLogicContext must be used within FilterLogicProvider. ' +
				'Make sure your app is wrapped with <FilterLogicProvider>',
		);
	}
	return context;
};

/**
 * For backward compatibility with existing components
 * that still use useFilterLogic() directly
 * This keeps working but should be migrated to useFilterLogicContext()
 *
 * Note: This ALWAYS calls useFilterLogic() first (hook rule compliance)
 * but prefers context if available
 */
export const useFilterLogicCompat = () => {
	// Always call hook first (hooks must be called unconditionally)
	const filterLogic = useFilterLogic();

	// Try to get from context (optimal)
	try {
		const context = useFilterLogicContext();
		return context;
	} catch (e) {
		// Context not available, use direct hook we already initialized
		console.warn(
			'Component using useFilterLogic() without FilterLogicProvider. ' +
				'This creates additional hook instances and API calls. ' +
				'Please wrap your app with <FilterLogicProvider> and use useFilterLogicContext() instead.',
		);
		return filterLogic;
	}
};
